import { useState } from 'react'
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material'
import {
  Mic as MicIcon,
  MicOff as MicOffIcon,
  CallEnd as CallEndIcon,
} from '@mui/icons-material'
import {
  useMockInterviewSession,
  type MockSessionState,
} from '../../hooks/useMockInterviewSession'
import DialogShell from '../common/DialogShell'

interface MockInterviewVoiceProps {
  interviewId: string
  /** True when the unlock gate passes — prep guide exists, published, and viewed. */
  canStart: boolean
  /** Shown as a disabled tooltip when canStart is false. */
  lockedReason?: string
  /** Called after a session ends so the parent can refetch the interview. */
  onSessionEnded?: (args: { sessionId: string; durationSeconds: number; endedReason: string }) => void
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

const STATE_STATUS_TEXT: Record<MockSessionState, string> = {
  idle: 'Ready to begin',
  requesting_mic: 'Requesting microphone access...',
  connecting: 'Connecting...',
  listening: 'Listening',
  speaking: 'Interviewer speaking',
  ending: 'Wrapping up...',
  ended: 'Interview complete',
  error: 'Something went wrong',
}

export default function MockInterviewVoice({
  interviewId,
  canStart,
  lockedReason,
  onSessionEnded,
}: MockInterviewVoiceProps) {
  const [consentOpen, setConsentOpen] = useState(false)

  const {
    state,
    errorMessage,
    transcript,
    elapsedSeconds,
    muted,
    startSession,
    endSession,
    toggleMute,
  } = useMockInterviewSession({
    interviewId,
    onEnded: onSessionEnded,
  })

  const isActive =
    state === 'connecting' ||
    state === 'listening' ||
    state === 'speaking' ||
    state === 'requesting_mic'

  const handleStartClick = () => {
    if (!canStart) return
    setConsentOpen(true)
  }

  const handleConsentAgree = () => {
    setConsentOpen(false)
    startSession()
  }

  // Idle / pre-session UI
  if (!isActive && state !== 'ending') {
    const title =
      state === 'ended' ? 'Interview complete' : 'AI Voice Mock Interview'
    const body =
      state === 'ended'
        ? 'Session complete. Your feedback will appear below shortly.'
        : 'A voice-based mock interview built around your role, company, and prep guide. Feedback is ready when the session ends.'

    return (
      <>
        <Box
          sx={{
            p: 3,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'grey.50',
          }}
        >
          <MicIcon
            sx={{
              fontSize: 40,
              color: canStart ? 'primary.main' : 'text.disabled',
              mb: 1,
            }}
          />
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 440, mx: 'auto', mb: 2 }}>
            {body}
          </Typography>
          {state === 'error' && errorMessage && (
            <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
              {errorMessage}
            </Alert>
          )}
          <Tooltip title={!canStart ? lockedReason || 'Not available yet' : ''} arrow>
            <span>
              <Button
                variant="contained"
                startIcon={<MicIcon />}
                onClick={handleStartClick}
                disabled={!canStart}
              >
                {state === 'ended' ? 'Start Another Mock' : 'Start Mock Interview'}
              </Button>
            </span>
          </Tooltip>
        </Box>

        {/* Consent dialog */}
        <DialogShell
          open={consentOpen}
          onClose={() => setConsentOpen(false)}
          maxWidth="xs"
          compact
          title="Before we start"
          footer={
            <>
              <Button onClick={() => setConsentOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={handleConsentAgree}>
                I understand — start
              </Button>
            </>
          }
        >
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Before you begin, a few things to know:
          </Typography>
          <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { mb: 0.75 } }}>
            <li>
              <Typography variant="body2">
                Your voice will be transcribed for you and your coach to review.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Audio is <strong>not</strong> recorded — only the text transcript is saved.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                You can end the session at any time using the red end-call button.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Sessions are capped at 15 minutes.
              </Typography>
            </li>
          </Box>
        </DialogShell>
      </>
    )
  }

  // Active-session UI
  return (
    <Box
      sx={{
        p: 3,
        border: '1px solid',
        borderColor: 'primary.light',
        borderRadius: 2,
        bgcolor: 'primary.50',
        position: 'relative',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            bgcolor: state === 'speaking' ? 'primary.main' : 'primary.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: state === 'speaking' ? 3 : 1,
            transition: 'all 200ms ease',
          }}
        >
          {state === 'connecting' || state === 'requesting_mic' ? (
            <CircularProgress size={24} sx={{ color: 'common.white' }} />
          ) : (
            <MicIcon sx={{ fontSize: 28, color: 'common.white' }} />
          )}
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Mock Interview in Progress
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Chip
              label={STATE_STATUS_TEXT[state]}
              size="small"
              color={state === 'listening' ? 'success' : 'primary'}
              variant="outlined"
            />
            <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatTime(elapsedSeconds)} / 15:00
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={toggleMute}
          aria-label={muted ? 'Unmute microphone' : 'Mute microphone'}
          color={muted ? 'warning' : 'default'}
        >
          {muted ? <MicOffIcon /> : <MicIcon />}
        </IconButton>
        <IconButton
          onClick={() => endSession('client_request')}
          aria-label="End mock interview"
          color="error"
          disabled={state === 'ending'}
        >
          <CallEndIcon />
        </IconButton>
      </Box>

      {/* Live transcript — visually-hidden for screen readers, visible list for sighted users */}
      {transcript.length > 0 && (
        <Box
          sx={{
            maxHeight: 180,
            overflowY: 'auto',
            bgcolor: 'background.paper',
            p: 1.5,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            '& .entry': { mb: 1 },
            '& .entry:last-child': { mb: 0 },
          }}
          aria-live="polite"
        >
          {transcript.map((t, i) => (
            <Box key={i} className="entry">
              <Typography
                component="span"
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: t.role === 'assistant' ? 'primary.main' : 'text.secondary',
                  mr: 0.75,
                }}
              >
                {t.role === 'assistant' ? 'Interviewer:' : 'You:'}
              </Typography>
              <Typography component="span" variant="body2">
                {t.content}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}
